import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {DEFAULT_ANIMATION_DURATION} from '@Components/modal';
import styles from './feedback-popup.module.scss';
import {Text, TextSize, TextType} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import type {
  FeedbackPopupProps,
  NegativeFeedbackSuccessPanel,
  NegativeFeedbackSuggestion,
  SuggestionPointType,
} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup.types';
import {
  AIChatNegativeFeedbackTypes,
  FEEDBACK_COMMENT_MAX_LENGTH,
  getSuggestions,
  SuggestionActionKey,
  SuggestionPointTypes,
} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup.types';
import {FeedbackType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {RadioButtonOption} from '@Components/radio-button';
import {RadioButton} from '@Components/radio-button';
import {InputField} from '@Components/input-field-v2';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {AiIcon} from '@Components/ai-icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {ThemeGrid} from '@Components/ai-chat-window/components/change-style-dropdown/components/theme-grid/theme-grid';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {MODALS_IDS} from '@Components/modal-container';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {useModalData} from '@Components/modal/hooks/useModalData';
import type {MoreOptionsPanelProps} from '@Panels/more-options-panel/more-options-panel';
import {useAppDispatch} from '@/hooks';
import {closeAllModals} from '@Components/modal-container/modal-container-reducer';
import {replaceStringWithReactNode} from '@Utils/string.util';
import {StaticDot} from '@Components/static-dot/static-dot';
import {StaticDotSize, StaticDotTheme} from '@Components/static-dot/static-dot.types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

export function FeedbackPopup({...props}: FeedbackPopupProps): ReactNode {
  const [feedbackText, setFeedbackText] = useState<InputType>('');
  const [activeSelectedNegativeFeedback, setActiveSelectedNegativeFeedback] = useState<AIChatNegativeFeedbackTypes | null>(props.initialNegativeFeedbackPreset ?? null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(!!props.isAlreadySubmitted);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);
  const modalData = useModalData(MODALS_IDS.MORE_OPTIONS_MODAL);
  const dispatch = useAppDispatch();

  const closeSuccessModal = (): void => {
    dispatch(closeAllModals());
  };

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    if (showThemePicker) {
      openMoreOptionsModal({
        ...(modalData.panelProps as MoreOptionsPanelProps),
        headerTitle: window.i18next.t('pmwjs_ai_chat_change_style_heading'),
        showBackButton: true,
        closeModalOnBack: false,
        onBack: () => {
          setShowThemePicker(false);
        },
      });
      return;
    }

    if (showSuccessScreen) {
      openMoreOptionsModal({
        ...(modalData.panelProps as MoreOptionsPanelProps),
        headerTitle: window.i18next.t('pmwjs_thanks_for_letting_us_know'),
        showBackButton: false,
      });
    }
  }, [showSuccessScreen, showThemePicker, isMobile]);

  const onSubmit = (): void => {
    if (!activeSelectedNegativeFeedback) {
      return;
    }

    props.onSubmitFeedback(FeedbackType.NEGATIVE, activeSelectedNegativeFeedback, feedbackText as string);
    setShowSuccessScreen(true);
  };

  const handleFeedbackOptionChange = (selectedId: string): void => {
    setActiveSelectedNegativeFeedback(selectedId as AIChatNegativeFeedbackTypes);
  };

  const options: RadioButtonOption[] = [
    {id: AIChatNegativeFeedbackTypes.DIDNT_FOLLOW_INSTRUCTIONS, text: window.i18next.t('pmwjs_didnt_follow_instructions')},
    {id: AIChatNegativeFeedbackTypes.TEXT_OR_SPELLING_ERRORS, text: window.i18next.t('pmwjs_text_or_spelling_errors')},
    {id: AIChatNegativeFeedbackTypes.DONT_LIKE_DESIGN, text: window.i18next.t('pmwjs_dont_like_design')},
    {id: AIChatNegativeFeedbackTypes.OTHER, text: window.i18next.t('pmwjs_other')},
  ];

  const renderForm = (): ReactElement => {
    return (
      <div className={`flex-v-row gap-2 ${styles.form}`}>
        <div className={'spacing-p-l-1 spacing-p-r-1 flex-v-row gap-2'}>
          {renderHeading(window.i18next.t('pmwjs_help_us_improve'))}
          <RadioButton
            options={options}
            onSelectionCallback={handleFeedbackOptionChange}
            selectionId={activeSelectedNegativeFeedback ?? ''}
            isTextClickable
            containerClasses={styles.options}
            textClasses={'body-sm-regular spacing-m-l-2'}
          />
        </div>

        <InputField
          placeholder={window.i18next.t('pmwjs_tell_us_about_your_experience')}
          maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
          isMultiline
          onInputSubmit={onSubmit}
          onInputChange={setFeedbackText}
          input={feedbackText}
          inputClassName={`radius-8 ${styles.textInput} body-sm-regular`}
        />
        <Button
          size={isMobile ? Size.SMALL : Size.XSMALL}
          isFullWidth={isMobile}
          text={window.i18next.t('pmwjs_submit')}
          type={Type.PRIMARY}
          onClick={onSubmit}
          disabled={!activeSelectedNegativeFeedback}
          customClasses={isMobile ? '' : '_fit-width spacing-m-l-auto'}
        />
      </div>
    );
  };

  const renderSuggestionPoint = (suggestion: SuggestionPointType): ReactNode => {
    if (suggestion.type === SuggestionPointTypes.STRING) {
      return <Text val={suggestion.content} size={TextSize.XXSMALL} />;
    }

    const {actionKey, content} = suggestion;

    const actionConfigs: Record<SuggestionActionKey, {onClick?: VoidFunction; label: string}> = {
      [SuggestionActionKey.CHANGE_STYLE]: {
        onClick: () => {
          setShowThemePicker(true);
        },
        label: 'pmwjs_change_style',
      },
      [SuggestionActionKey.TEMPLATE]: {onClick: props.onClickTemplate, label: 'pmwjs_template'},
      [SuggestionActionKey.EDIT_IN_EDITOR]: {onClick: props.onClickEditInEditor, label: 'pmwjs_edit_in_editor'},
    };

    const config = actionConfigs[actionKey];
    const handleClick = (): void => {
      if (actionKey === SuggestionActionKey.CHANGE_STYLE) {
        config.onClick?.();
        return;
      }

      if (isMobile) {
        closeSuccessModal();
        setTimeout(() => {
          config.onClick?.();
        }, DEFAULT_ANIMATION_DURATION);
      } else {
        config.onClick?.();
      }
    };

    const actionLabel = window.i18next.t(config.label);
    const isChangeStyleDisabled = actionKey === SuggestionActionKey.CHANGE_STYLE && (!!props.isMessageStreaming || !props.canWrite);

    const actionText = (
      <Text
        className={`_text-underline _cursor-pointer ${isChangeStyleDisabled ? 'ct-neutral-primary-disabled' : 'content-link'}`}
        val={actionLabel}
        size={TextSize.XXSMALL}
        onClick={isChangeStyleDisabled ? undefined : handleClick}
        bold
      />
    );

    const showStreamingTooltip = isChangeStyleDisabled && props.canWrite;
    const actionNode = showStreamingTooltip ? (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className="spacing-m-b-1 _rounded-arrow">{window.i18next.t('pmwjs_change_style_unavailable_while_generating')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
      >
        <span>{actionText}</span>
      </OverlayTrigger>
    ) : (
      actionText
    );

    return <span className="body-sm-regular">{replaceStringWithReactNode(content, actionKey, actionNode)}</span>;
  };

  const renderGotItButton = (): ReactElement | null => {
    if (!isMobile) {
      return null;
    }

    return <Button type={Type.PRIMARY} onClick={closeSuccessModal} text={window.i18next.t('pmwjs_got_it')} size={Size.SMALL} customClasses="spacing-m-t-4" />;
  };

  const renderSolutionPoints = (issue: NegativeFeedbackSuggestion, issueIndex: number): ReactElement => {
    return (
      <div key={issueIndex} className="spacing-m-b-2">
        <div className="flex-row-align-center gap-2 spacing-m-b-2">
          <AiIcon icon={issue.icon} size={IconSize.SIZE_ICON_16} className="radius-8" />
          <Text val={issue.heading} size={TextSize.XSMALL} bold />
        </div>
        <div className="flex-v-row gap-2">
          {issue.points.map((suggestion, suggestionIndex) => {
            return (
              <div key={suggestionIndex} className={`flexbox gap-2 ${styles.bulletItem}`}>
                <div className={styles.dotContainer}>
                  <StaticDot size={StaticDotSize.SIZE_4} theme={StaticDotTheme.DARK} />
                </div>
                {renderSuggestionPoint(suggestion)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const buildNegativeFeedbackRecordedSuccessScreen = (negativeFeedbackSuccessPanel: NegativeFeedbackSuccessPanel): ReactElement => {
    return (
      <div className="spacing-p-l-1 spacing-p-r-1 flex-v-row gap-2 content-body">
        {renderHeading(window.i18next.t('pmwjs_thanks_for_letting_us_know'))}
        <Text val={negativeFeedbackSuccessPanel.subtext} size={TextSize.XXSMALL} className="spacing-m-b-2" />
        {negativeFeedbackSuccessPanel.sugesstions.map(renderSolutionPoints)}
        {renderGotItButton()}
      </div>
    );
  };

  const renderSuccessScreen = (feedbackPreset: AIChatNegativeFeedbackTypes): ReactElement => {
    return buildNegativeFeedbackRecordedSuccessScreen(getSuggestions(feedbackPreset));
  };

  const renderHeading = (text: string): ReactElement | null => {
    if (isMobile) {
      return null;
    }

    return <Text val={text} size={TextSize.XSMALL} type={TextType.BODY} bold />;
  };

  const renderThemePicker = (): ReactElement => {
    return (
      <div className={`flex-v-row gap-3 ${isMobile ? styles.themePickerModalContent : ''}`}>
        {!isMobile ? (
          <div className="flex-row-align-center gap-2">
            <Icon
              icon="icon-arrow-left"
              size={IconSize.SIZE_ICON_16}
              type={IconType.GHOST}
              shape={IconShape.CIRCLE}
              onClick={(): void => {
                setShowThemePicker(false);
              }}
            />
            <Text val={window.i18next.t('pmwjs_ai_chat_change_style_heading')} size={TextSize.MD} bold />
          </div>
        ) : null}
        <ThemeGrid
          onThemeSelect={(theme): void => {
            setShowThemePicker(false);
            props.onChangeStyleThemeSelect(theme);
          }}
        />
      </div>
    );
  };

  const getContent = (): ReactElement => {
    if (showThemePicker) {
      return renderThemePicker();
    }

    if (showSuccessScreen && activeSelectedNegativeFeedback) {
      return renderSuccessScreen(activeSelectedNegativeFeedback);
    }

    return renderForm();
  };

  return <div className={isMobile ? '' : 'spacing-p-2'}>{getContent()}</div>;
}
